import { BREAKPOINTS, TextButton as Button, COLORS, HyperLink, SystemIcons } from '@laerdal/life-react-components';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { PageWidthFull } from './PageWidthFull';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectMarket, buildMarketPath } from '../store/slices/globalConfigSlice';
import { useState, useEffect } from 'react';
import posthog from 'posthog-js';
import { useMediaMatch } from 'rooks';


const FullPageContainer = styled.div`
  display:flex;
  background: ${COLORS.neutral_20};
  border-bottom: 1px solid #CCC;
`;

const DownloadLink = styled(HyperLink)`
    font-size: 20px;

    svg {
      padding-bottom: 7px;
      vertical-align: middle;
    }
`;

const Breadcrumb = styled.div`
  padding:12px 0;
  margin-top: 32px;
`;
const ColumnContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 32px;
  margin-bottom: 32px;
`;
const LeftColumn = styled.div`
  display:flex;
  flex-direction:column;
  gap: 16px;
  flex: 1;
  align-items: start;
`;
const RightColumn = styled.div`
  display: none;
  ${BREAKPOINTS.MEDIUM}{
    display:flex;
    align-items: end;
  }
  justify-content: center;
  flex-direction: column;
  flex: 1;
  img { 
    width: 80%;
  }
`;
const Title = styled.div`
  color: #1F1F1F;
  font-feature-settings: 'liga' off;
  font-family: Lato;
  font-size: 32px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  ${BREAKPOINTS.MEDIUM}{
    color: var(--life-color-text-default);
    font-size: 40px;
  }
`;
const Description = styled.div`
  color: #1F1F1F;
  font-feature-settings: 'liga' off;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  ${BREAKPOINTS.MEDIUM}{
    color: var(--life-color-text-default);
    font-size: 20px;
  }
`;


const CrumbLink = styled.a`
  &:link, &:visited, &:hover, &:active {
    color: ${COLORS.primary_600};
    text-decoration: none; 
  }

  color: ${COLORS.primary_600};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 25px; 
`;

const CrumbText = styled.span`
  color: ${COLORS.neutral_500};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 25px; 
`;

const Devider = styled.span`
  padding: 0 10px;
  color: ${COLORS.neutral_500};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 25px; 
`;

interface Props {
  title: string,
  description: string,
  asset: string,
  buttonText?: string,
  buttonLink?: string,
  paligoUserGuideUrl?: string,
  breadcrumb: Crumb[],
  hasDownloads?: boolean,
  productLineCode?: string
}
interface Crumb {
  text: string;
  link?: string;
}

const ProductTopSection = (props: Props) => {
  const { t } = useTranslation('translation');
  const navigate = useNavigate()
  const currentMartket = useSelector(selectMarket);
  const [downloadLink, setDownloadLink] = useState<string>('');
  const isMediumScreen = useMediaMatch(BREAKPOINTS.MEDIUM.replace('@media ', ''));
  const isMobile = !isMediumScreen;


  useEffect(() => {
    if (props.hasDownloads && props.productLineCode && process.env.REACT_APP_DOWNLOAD_LINK?.toString() === '/downloads') {
      setDownloadLink(currentMartket ?
        `/${currentMartket}/downloads/${props.productLineCode}`
        : `/downloads/${props.productLineCode}`);
    }
    else
      setDownloadLink('');

  }, [currentMartket, props.hasDownloads, props.productLineCode]);



  return (
    <FullPageContainer>
      <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $unsetMargin={true}>
        <Breadcrumb>
          {props.breadcrumb.map((item, index) =>
            <>
              {!!item.link &&
                <CrumbLink
                  key={`breadcrumb-href-${index}`}
                  href={item.link}
                  onClick={(e) => {
                    e.preventDefault();
                    posthog.capture('SUPP-BreadcrumbClick', {
                      currentUrl: window.location.href,
                      clickedLink: item.link,
                      step: 1
                    });
                    navigate(item.link!);
                  }}
                >
                  {item.text}
                </CrumbLink>
              }
              {!item.link &&
                <CrumbText
                  key={`breadcrumb-text-${index}`}>
                  {item.text}
                </CrumbText>
              }
              {(props.breadcrumb.length != index + 1) &&
                <Devider
                  key={`breadcrumb-devider-${index}`}>/</Devider>
              }
            </>
          )
          }
        </Breadcrumb>
        <ColumnContainer>
          <LeftColumn>
            <Title>{props.title}</Title>
            <Description dangerouslySetInnerHTML={{ __html: props.description }}>
              
            </Description>
            {!!props.hasDownloads && props.productLineCode && !!downloadLink &&
              <>
                <DownloadLink
                  href={downloadLink}
                  variant='default'
                  onClick={(e) => {
                    e.preventDefault()
                    // @ts-ignore
                    posthog.capture('SUPP-ProductPageDownloadLink', {
                      title: props.title,
                      currentUrl: window.location.href,
                      targetUrl: downloadLink,
                    });
                    navigate(`${window.location.pathname}/downloads`);
                  }}
                >
                  {t('Downloads')} <SystemIcons.OpenNewWindow size='28px' />
                </DownloadLink>
              </>
            }
            {props.productLineCode == 'qcpr-app' && isMobile &&
              <Button
                variant='secondary'
                onClick={() => {
                  // @ts-ignore
                  posthog.capture('SUPP-ProductTopSection-TroubleshootingGuideClick', {
                    title: props.title,
                    productLineCode: props.productLineCode,
                  });
                  navigate(buildMarketPath(currentMartket, '/qcpr-support/troubleshooting'))
                }}
              >
                {t('Troubleshooting Guide')}
              </Button>}
          </LeftColumn>
          {!!props.asset && <RightColumn>
            <img src={props.asset} alt={props.title} />
          </RightColumn>}
        </ColumnContainer>
      </PageWidthFull>

    </FullPageContainer>
  );
}

export default ProductTopSection;